/**
 * VaultTipTapEditor — TipTap host with our four Obsidian extensions.
 *
 * Loads the note body via `markdownToDoc`, listens for changes, and
 * fires `onChangeMarkdown` with a serialized round-trip back to plain
 * markdown via `docToMarkdown`. C.9 wires the debounced PUT-to-
 * SharePoint path through this callback.
 *
 * StarterKit gives us headings, paragraphs, lists, code, bold/italic,
 * blockquotes, hard break, history. We add our four Obsidian
 * extensions on top.
 */

import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { useEffect, useMemo, useRef } from 'react';
import type { ParsedNote } from '../../lib/vault/parse';
import {
  CalloutBlock,
  EmbedBlock,
  ObsidianTag,
  WikilinkMark,
} from '../../lib/vault/tiptap';
import { docToMarkdown, markdownToDoc } from '../../lib/vault/tiptap/markdownDoc';
import { DataviewBlockNode } from './dataview/DataviewBlockNode';

export interface VaultTipTapEditorProps {
  note: ParsedNote;
  onChangeMarkdown?: (markdown: string) => void;
  /**
   * Notes the dataview runner can query. Wired through `editor.storage`
   * so the React node view can reach them without prop drilling per
   * block. Pass the full vault notes list.
   */
  vaultNotes?: ReadonlyArray<ParsedNote>;
  /**
   * Optional link click handler. When a Dataview cell rendering
   * `file.link` is clicked, this fires with the target note path.
   * Modifier-click (cmd/ctrl/shift) routes to pane B.
   */
  onOpenLink?: (path: string, modifier: 'pane-a' | 'pane-b') => void;
}

export function VaultTipTapEditor({
  note,
  onChangeMarkdown,
  vaultNotes,
  onOpenLink,
}: VaultTipTapEditorProps) {
  const initialDoc = useMemo(() => markdownToDoc(note.body), [note.body]);
  const lastEmittedRef = useRef<string>(note.body);

  const editor = useEditor(
    {
      extensions: [
        StarterKit.configure({
          // We use our own callout node, so suppress the default
          // blockquote-everywhere fallback.
          blockquote: false,
        }),
        WikilinkMark,
        EmbedBlock,
        ObsidianTag,
        CalloutBlock,
        DataviewBlockNode,
      ],
      content: initialDoc,
      onUpdate({ editor }) {
        if (!onChangeMarkdown) return;
        const json = editor.getJSON();
        const md = docToMarkdown(json as never);
        if (md !== lastEmittedRef.current) {
          lastEmittedRef.current = md;
          onChangeMarkdown(md);
        }
      },
    },
    // Re-create the editor when the active note changes so doc state
    // doesn't bleed across notes (TipTap 3.x supports key-based reset
    // but the deps array is the canonical handle here).
    [note.path],
  );

  // Wire the dataview storage so the React node view can reach the
  // vault notes + link handler without prop drilling. Updated whenever
  // either changes so dataview blocks re-execute against fresh data.
  useEffect(() => {
    if (!editor) return;
    const storage = editor.extensionStorage as unknown as Record<string, unknown>;
    storage['dataviewBlock'] = {
      notes: vaultNotes ?? [],
      onOpenLink,
    };
  }, [editor, vaultNotes, onOpenLink]);

  // Reset content when the same note's body changes externally (e.g.,
  // a Graph delta brought in a newer version while we had the note open
  // unedited). The editor takes the new doc and resets `lastEmittedRef`
  // so we don't loop a no-op save.
  useEffect(() => {
    if (!editor) return;
    const currentMd = docToMarkdown(editor.getJSON() as never);
    if (currentMd.trim() === note.body.trim()) return;
    editor.commands.setContent(initialDoc, { emitUpdate: false });
    lastEmittedRef.current = note.body;
  }, [editor, initialDoc, note.body]);

  if (!editor) return null;
  return (
    <div className="vault-tiptap-root">
      <EditorContent editor={editor} />
    </div>
  );
}
